import { useMemo } from 'react';
import { GridCellProps } from 'react-virtualized';
import * as MultiGridStyles from '~/components/MultiGrid/styles';
import { INVOICE_ALL_FIELDS, INVOICE_FIELDS_ORDER } from './constants';
import { CELL_SETTINGS } from '~/components/MultiGrid/constants';
import HeaderContent from './HeaderContent';
import CellContent from './CellContent';
import { useClientListContext } from './hooks/useClientListContext';

export interface ICellContentProps {
  multiGridSettings: Readonly<{
    fixedRowCount: number;
    fixedColumnCount: number;
  }>;
  handleOpenClient(clientReferenceNumber: string): void;
}

interface IGridCellProps {
  gridProps: GridCellProps;
  cellProps: ICellContentProps;
}

const GridCell = ({ gridProps: { columnIndex, rowIndex, style }, cellProps }: IGridCellProps) => {
  const { listIds, listData } = useClientListContext();

  const thisClient = listData[listIds[rowIndex - 1]];

  const field = INVOICE_ALL_FIELDS[INVOICE_FIELDS_ORDER[columnIndex]];
  const keyName = field.value;

  const { multiGridSettings } = cellProps;

  const rowIndexType = useMemo(() => {
    if (rowIndex === 0) {
      return 'first';
    }
    return rowIndex === listIds.length ? 'last' : null;
  }, [rowIndex, listIds.length]);

  const columnIndexType = useMemo(() => {
    if (columnIndex === 0) {
      return 'first';
    }
    return columnIndex === INVOICE_FIELDS_ORDER.length - 1 ? 'last' : null;
  }, [columnIndex]);

  const isLastStaticColumn = useMemo(
    () => columnIndex === multiGridSettings.fixedColumnCount - 1,
    [columnIndex, multiGridSettings.fixedColumnCount],
  );

  if (rowIndex === 0) {
    return (
      <MultiGridStyles.HeaderCell
        columnIndexType={columnIndexType}
        style={style}
        {...{ [CELL_SETTINGS.lastColumnDataAttribute]: isLastStaticColumn }}
      >
        <HeaderContent
          fieldValue={field.value}
          fieldLabel={field.label}
          showFilter={field.showFilter ?? false}
          isSortable={field.isSortable ?? false}
        />
        {isLastStaticColumn && <MultiGridStyles.AbsoluteBorder />}
      </MultiGridStyles.HeaderCell>
    );
  }

  if (thisClient) {
    return (
      <MultiGridStyles.BodyCell
        className="align-center"
        rowIndexType={rowIndexType}
        columnIndexType={columnIndexType}
        paddingLayer={field.isCellFullWidth ? 'first_child' : 'self'}
        style={style}
        {...{ [CELL_SETTINGS.lastColumnDataAttribute]: isLastStaticColumn }}
      >
        <CellContent
          clientReferenceId={thisClient.business_client_uuid}
          identifier={keyName}
          {...cellProps}
        />
        {isLastStaticColumn && <MultiGridStyles.AbsoluteBorder />}
      </MultiGridStyles.BodyCell>
    );
  }

  return null;
};

export default GridCell;
